import { useState, useEffect, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow, format, isToday, isYesterday } from 'date-fns'
import type { TFunction } from 'i18next'
import { getDateFnsLocale, uses24HourFormat, getBaseLanguage } from '@/lib/i18n'

interface RelativeTimeProps {
  timestamp: string
  className?: string
}

function formatMessageTime(timestamp: string, t: TFunction): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diffInMinutes = (now.getTime() - date.getTime()) / 1000 / 60
  const locale = getDateFnsLocale()
  const is24Hour = uses24HourFormat()

  // Time format: 24-hour for Russian, 12-hour AM/PM for others
  const timeFormat = is24Hour ? 'HH:mm' : 'h:mm a'

  // Less than 1 minute: "just now"
  if (diffInMinutes < 1) {
    return t('chat.justNow')
  }

  // Less than 1 hour: relative time (e.g., "5 minutes ago")
  if (diffInMinutes < 60) {
    return formatDistanceToNow(date, { addSuffix: true, locale })
  }

  // Today: time only (e.g., "3:45 PM" or "15:45")
  if (isToday(date)) {
    return format(date, timeFormat, { locale })
  }

  // Yesterday: "Yesterday at 3:45 PM" or "Вчера в 15:45"
  if (isYesterday(date)) {
    return t('chat.yesterdayAt', { time: format(date, timeFormat, { locale }) })
  }

  // This year: "Jan 24, 3:45 PM" or "24 янв., 15:45"
  if (date.getFullYear() === now.getFullYear()) {
    return is24Hour
      ? format(date, 'd MMM, HH:mm', { locale })
      : format(date, 'MMM d, h:mm a', { locale })
  }

  // Previous years: "Jan 24, 2024, 3:45 PM" or "24 янв. 2024, 15:45"
  return is24Hour
    ? format(date, 'd MMM yyyy, HH:mm', { locale })
    : format(date, 'MMM d, yyyy, h:mm a', { locale })
}

function isRelativeTime(timestamp: string): boolean {
  const diffInMinutes = (Date.now() - new Date(timestamp).getTime()) / 1000 / 60
  return diffInMinutes < 60
}

export const RelativeTime = memo(function RelativeTime({
  timestamp,
  className,
}: RelativeTimeProps) {
  const { t } = useTranslation('conversations')
  const lang = getBaseLanguage() // Keep for useEffect dependency to re-render on language change

  const [formattedTime, setFormattedTime] = useState(() =>
    formatMessageTime(timestamp, t)
  )

  useEffect(() => {
    setFormattedTime(formatMessageTime(timestamp, t))

    // Only set up interval for relative times (< 1 hour old)
    if (!isRelativeTime(timestamp)) {
      return
    }

    const intervalId = setInterval(() => {
      setFormattedTime(formatMessageTime(timestamp, t))

      // Stop updating once timestamp becomes absolute
      if (!isRelativeTime(timestamp)) {
        clearInterval(intervalId)
      }
    }, 60_000)

    return () => clearInterval(intervalId)
  }, [timestamp, t, lang])

  return <span className={className}>{formattedTime}</span>
})
